// src/components/layouts/Sidebar.tsx
import React, { useState, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import './styles/Layout.css';
import useAuthService from '../../services/authService';
import LogoutConfirmModal from './LogoutConfirmModal';
import ChangePasswordModal from './ChangePasswordModal';
import UserStatus from './UserStatus';
import { canAccessEmailsModule, canAccessNotificationPreferencesModule } from '../../hooks/usePermissions';
import { getSessionUser } from '../../services/authSession';
import TzBrandMark from '../auth/TzBrandMark';
import {
  LayoutDashboard,
  Briefcase,
  Users,
  Building2,
  ShieldCheck,
  UserCheck,
  Calculator,
  Ticket,
  UserCircle,
  Clock,
  UserCog,
  Car,
  MapPin,
  Search,
  X,
  LogOut,
  KeyRound,
  ChevronRight,
  ChevronLeft,
  BookOpen,
  Layers,
  Mail,
  BellRing
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  isCollapsed: boolean;
  setIsCollapsed: (value: boolean) => void;
}

interface MenuItemConfig {
  path: string;
  label: string;
  iconComponent: React.ComponentType<{ size?: number }>;
  category?: string;
  canView?: () => boolean;
}

// Menu items configuración (sin renderizar iconos)
const MENU_ITEMS_CONFIG: MenuItemConfig[] = [
  { path: '/dashboard', label: 'Dashboard', iconComponent: LayoutDashboard },
  { path: '/services', label: 'Servicios', iconComponent: Briefcase, category: 'Operaciones' },
  { path: '/emails', label: 'Correos', iconComponent: Mail, category: 'Operaciones', canView: canAccessEmailsModule },
  { path: '/companies', label: 'Empresas', iconComponent: Building2, category: 'Administración' },
  { path: '/authorizers', label: 'Autorizadores', iconComponent: ShieldCheck, category: 'Administración' },
  { path: '/requesters', label: 'Solicitantes', iconComponent: UserCheck, category: 'Administración' },
  { path: '/cost-center', label: 'Centro de costo', iconComponent: Calculator, category: 'Administración' },
  { path: '/el-abra', label: 'El Abra', iconComponent: Layers, category: 'Administración' },
  { path: '/users', label: 'Usuarios', iconComponent: Users, category: 'Administración' },
  {
    path: '/notification-preferences',
    label: 'Notificaciones',
    iconComponent: BellRing,
    category: 'Administración',
    canView: canAccessNotificationPreferencesModule,
  },
  { path: '/passenger', label: 'Pasajeros', iconComponent: UserCircle, category: 'Operaciones' },
  { path: '/location', label: 'Ubicaciones', iconComponent: MapPin, category: 'Operaciones' },
  { path: '/drivers', label: 'Conductores', iconComponent: UserCog, category: 'Operaciones' },
  { path: '/vehicles', label: 'Vehículos', iconComponent: Car, category: 'Operaciones' },
  { path: '/passes', label: 'Pases', iconComponent: Ticket, category: 'Operaciones' },
  { path: '/shifts', label: 'Turnos', iconComponent: Clock, category: 'Operaciones' },
  // { path: '/guide', label: 'Guía de uso', iconComponent: BookOpen, category: 'Ayuda' },
];

const Sidebar: React.FC<SidebarProps> = ({ isOpen, setIsOpen, isCollapsed, setIsCollapsed }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const { logout } = useAuthService();
  const sessionUser = getSessionUser<{ id?: number; role?: string; is_superuser?: boolean }>();
  const sessionKey = `${sessionUser?.id ?? ''}:${sessionUser?.role ?? ''}:${sessionUser?.is_superuser ? '1' : '0'}`;

  // Convertir configuración a items con iconos renderizados (recalcula al cambiar de sesión/rol)
  const menuItems = useMemo(() => {
    return MENU_ITEMS_CONFIG
      .filter((config) => !config.canView || config.canView())
      .map((config) => ({
        ...config,
        icon: <config.iconComponent size={20} />
      }));
  }, [sessionKey]);

  const filteredMenu = useMemo(() => {
    if (!searchTerm) return menuItems;
    return menuItems.filter((item) =>
      item.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, menuItems]);

  // Agrupar por categoría si no hay búsqueda
  const groupedMenu = useMemo(() => {
    if (searchTerm) return { '': filteredMenu };
    
    const groups: Record<string, typeof filteredMenu> = {};
    filteredMenu.forEach((item) => {
      const category = item.category || 'General';
      if (!groups[category]) groups[category] = [];
      groups[category].push(item);
    });
    return groups;
  }, [filteredMenu, searchTerm]);

  const handleLogoutClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    setShowLogoutModal(false);
    await logout();
  };

  const handleMobileClose = () => {
    if (window.innerWidth <= 768) {
      setIsOpen(false);
    }
  };

  return (
    <>
      <LogoutConfirmModal
        isOpen={showLogoutModal}
        onConfirm={handleLogoutConfirm}
        onCancel={() => setShowLogoutModal(false)}
      />
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />
      
      <aside className={`sidebar ${!isOpen ? 'closed' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
        {/* Header marca TZ */}
        <div className="sidebar-header">
          <div className="sidebar-logo-container" aria-hidden="true">
            <TzBrandMark className="sidebar-brand-mark" size={isCollapsed ? 48 : 64} />
          </div>
          {!isCollapsed && (
            <h2 className="sidebar-title">Transporte Zepeda</h2>
          )}
        </div>

        {/* Búsqueda mejorada - Ocultar cuando está colapsado */}
        {!isCollapsed && (
          <div className="sidebar-search">
            <div className="search-wrapper">
              <Search className="search-icon" size={0} />
              <input
                type="text"
                placeholder="Buscar menú..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
                aria-label="Buscar en el menú"
              />
              {searchTerm && (
                <button
                  className="search-clear"
                  onClick={() => setSearchTerm('')}
                  aria-label="Limpiar búsqueda"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Navegación mejorada */}
        <nav className="sidebar-nav" aria-label="Navegación principal">
          {filteredMenu.length > 0 ? (
            Object.entries(groupedMenu).map(([category, items]) => (
              <div key={category} className="menu-group">
                {category && !searchTerm && (
                  <div className="menu-category">{category}</div>
                )}
                {items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={handleMobileClose}
                    className={({ isActive }) => 
                      `nav-item ${isActive ? 'active' : ''}`
                    }
                    title={isCollapsed ? item.label : ''}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    {!isCollapsed && (
                      <>
                        <span className="nav-label">{item.label}</span>
                        <ChevronRight className="nav-chevron" size={16} />
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            ))
          ) : (
            <div className="no-results">
              <Search size={48} className="no-results-icon" />
              <p>No se encontraron resultados</p>
              <span>Intenta con otros términos de búsqueda</span>
            </div>
          )}
        </nav>

        {/* Footer mejorado */}
        <div className="sidebar-footer">
          {/* Botón para colapsar/expandir (solo desktop) */}
          <button
            className="sidebar-collapse-btn"
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label={isCollapsed ? 'Expandir menú' : 'Colapsar menú'}
            title={isCollapsed ? 'Expandir menú' : 'Colapsar menú'}
          >
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            {!isCollapsed && <span></span>}
          </button>
          <UserStatus isCollapsed={isCollapsed} />
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
